import React, { useState, useRef } from 'react'
import { SingleGoalUpdate } from 'Generated/graphql-types'
import { Button, Loading } from '@resiliatech/birdsong'
import { Divider } from '@mui/material'
import Pagination from '@mui/material/Pagination'
import { useMutation } from '@apollo/client'
import { useAppState } from 'Context/AppContext/AppContext'
import DELETE_GOAL_UPDATE from 'Apollo/Mutations/DeleteGoalUpdate'
import { GOAL_UPDATES } from 'Apollo/Queries/GoalUpdates'
import { showNewGoalsList, useAwsFlagActive } from 'Modules/FeatureFlag'
import ConfirmActionDialogSong from '../../../../SongComponents/ConfirmActionDialogSong/ConfirmActionDialogSong'
import TitleWithTooltipSong from '../../../../SongComponents/TitleWithTooltipSong/TitleWithTooltipSong'
import { ProgressUpdateDialog } from '../ProgressUpdate/ProgressUpdateDialog/ProgressUpdateDialog'
import {
  UpdatesTabContainer,
  Header,
  Subheader,
  HeaderAndButtonContainer,
  ButtonContainer,
  HeaderContainer,
  DialogAnchor,
  DividerContainer,
  UpdatesTable,
} from './UpdatesTab.styled'
import { Update } from './Update'
import { PaginationContainer } from '../CommentList/CommentList.styled'
import {
  dispatchAddUpdateClicked,
  dispatchProgressUpdateDeleted,
} from '../analytics.utils'
import { EmptyState } from './EmptyState'
import { GOALS } from '../../../../../Apollo/Queries/Goals'

type Props = {
  updates: SingleGoalUpdate[]
  updatesLoading: boolean
  goalId: number
  simple?: boolean
}

const MAX_UPDATES_PER_PAGE = 5

export const UpdatesTab = ({
  updates = [],
  updatesLoading,
  goalId,
  simple,
}: Props) => {
  const shouldShowNewGoalsList = useAwsFlagActive(showNewGoalsList)
  const { userConfig, nonprofitOrgConfig } = useAppState()
  const { id: userId } = userConfig
  const { orgId: nonprofitOrgId, platformPlan } = nonprofitOrgConfig

  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [isProgressUpdateOpen, setIsProgressUpdateOpen] = useState(false)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [activeUpdate, setActiveUpdate] = useState<SingleGoalUpdate>()
  const [page, setPage] = useState(1)

  const handleAddUpdate = () => {
    setIsProgressUpdateOpen(true)
    dispatchAddUpdateClicked({
      goalId,
      userId,
      platformPlan,
    })
  }

  const handleConfirmDelete = (update: SingleGoalUpdate) => {
    setActiveUpdate(update)
    setIsConfirmingDelete(true)
    dispatchProgressUpdateDeleted({
      progressUpdateId: update.id,
      goalId: update.goalId,
      goalTitle: update.goalTitle,
      userId,
      platformPlan,
      description: update.description,
    })
  }

  const [deleteGoalUpdate] = useMutation(DELETE_GOAL_UPDATE, {
    refetchQueries: [
      {
        query: GOAL_UPDATES,
        variables: { goalId, nonprofitOrgId },
      },
      {
        query: GOALS,
        variables: {
          activeOnly: true,
          goalId: goalId.toString(),
          nonprofitOrgId,
        },
      },
    ],
  })

  const handleDeleteUpdate = (goalUpdateId: number) => {
    deleteGoalUpdate({
      variables: { goalUpdateId, nonprofitOrgId },
    })
    setIsConfirmingDelete(false)
    setActiveUpdate(undefined)
  }

  const handleEditUpdate = (update: SingleGoalUpdate) => {
    setActiveUpdate(update)
    setIsProgressUpdateOpen(true)
  }

  const hasUpdates = !!updates.length
  const pageSize = simple ? 200 : MAX_UPDATES_PER_PAGE
  const pageCount = Math.ceil(updates.length / pageSize)
  const paginatedUpdates = updates.slice((page - 1) * pageSize, page * pageSize)
  const updatesCards = paginatedUpdates.map((update, i) => (
    <React.Fragment key={update.id}>
      <Update
        update={update}
        onDeleteUpdate={() => handleConfirmDelete(update)}
        onEditUpdate={() => handleEditUpdate(update)}
      />
      {paginatedUpdates.length - 1 !== i && (
        <DividerContainer>
          <Divider />
        </DividerContainer>
      )}
    </React.Fragment>
  ))
  return (
    <UpdatesTabContainer>
      <HeaderAndButtonContainer>
        {!simple && (
          <HeaderContainer>
            <Header>Progress updates</Header>
            <Subheader>
              As you work within the platform, we’ll ask you a few questions to
              help track your progress, your responses related to this Goal will
              be visible here by your whole team but only you can edit yours.
            </Subheader>
          </HeaderContainer>
        )}
        <ButtonContainer>
          <Button
            variant='primary'
            label={shouldShowNewGoalsList ? 'Add Snapshot' : 'Add Update'}
            onClick={handleAddUpdate}
          />
        </ButtonContainer>
      </HeaderAndButtonContainer>
      {updatesLoading && <Loading />}
      {hasUpdates ? (
        <UpdatesTable className='scroll'>
          {updatesCards}
          {pageCount > 1 && (
            <PaginationContainer>
              <Pagination
                count={pageCount}
                onChange={(_, value) => setPage(value)}
                page={page}
              />
            </PaginationContainer>
          )}
        </UpdatesTable>
      ) : (
        <EmptyState />
      )}
      <DialogAnchor ref={anchorRef}>
        <ProgressUpdateDialog
          goalId={goalId}
          goalUpdateId={activeUpdate?.id}
          goalUpdateDescription={activeUpdate?.description}
          open={isProgressUpdateOpen}
          onUpdateComplete={() => setActiveUpdate(undefined)}
          onClose={() => {
            setIsProgressUpdateOpen(false)
          }}
        />
      </DialogAnchor>
      {isConfirmingDelete && activeUpdate && (
        <ConfirmActionDialogSong
          title='Delete Update'
          open={isConfirmingDelete}
          submitButtonText='Delete'
          onCancelHandler={() => setIsConfirmingDelete(false)}
          onSubmitHandler={() => handleDeleteUpdate(activeUpdate.id)}
        >
          <TitleWithTooltipSong title='Are you sure you want to delete this update?' />
        </ConfirmActionDialogSong>
      )}
    </UpdatesTabContainer>
  )
}
